import assign from 'lodash/assign'
import capitalize from 'lodash/capitalize'
import isArray from 'lodash/isArray'
import isObject from 'lodash/isObject'
import maxBy from 'lodash/maxBy'
import sum from 'lodash/sum'

export const DEFAULT_MATCH_CRITERIA = {
  script: true,
  region: true,
  useDefault: true,
}

export class Locale {
  static parse(str) {
    str = this.normalize(str)
    let matches = str.match(
      /^([a-z]{2,3})(?:-([A-Z][a-z]{3}))?(?:-([A-Z0-9_-]{2,}|\d{3}))?$/,
    )
    if (!matches) {
      console.error(
        `Could not parse "${str}" as locale. Malformatted string.`,
      )
      return null
    }
    let data = {
      language: matches[1],
      script: matches[2],
      region: matches[3],
    }
    return new this(data)
  }
  static parseMultiple(array) {
    let result = array.map(this.parse.bind(this))
    result[0].isDefault = true
    return result
  }
  static normalize(str) {
    return str
      .trim()
      .replace(
        /^([a-z]{2,3})(?:[_-]([a-z]{4}))?(?:[_-]([a-z0-9-_]{2,}|\d{3}))?$/i,
        (...args) => {
          let matches = args.slice(1, -2)
          // let [offset, string] = args.slice(-2);
          return matches
            .map((value, index) => {
              if (!value) return
              switch (index) {
                case 1:
                  return capitalize(value)
                case 2:
                  return value.toUpperCase()
                default:
                  return value.toLowerCase()
              }
            })
            .filter(value => value)
            .join('-')
        },
      )
  }
  constructor(data) {
    if (typeof data === 'string') {
      return this.constructor.parse(data)
    }
    Object.assign(this, data)
  }
  toString() {
    return [this.language, this.script, this.region]
      .filter(value => value)
      .join('-')
  }
  format(strings, ...values) {
    return strings.reduce((result, string, index) => {
      if (values[index] && this[values[index]]) {
        return `${result}${string}${this[values[index]]}`
      }
      return result
    }, '')
  }
  match(otherLocales, criteria = {}) {
    if (!isArray(otherLocales)) {
      otherLocales = [otherLocales]
    }
    criteria = assign(
      {},
      DEFAULT_MATCH_CRITERIA,
      criteria,
      {
        language: true,
      },
    )
    let matchScore = otherLocale => {
      return sum([
        ...['language', 'script', 'region'].map(
          (key, index, keys) => {
            if (!criteria[key]) {
              return 0
            }
            if (this[key] === otherLocale[key]) {
              return 1 << (keys.length - index)
            }
            if (!this[key] || !this[key]) {
              return 0
            }
            return -1 * (1 << (keys.length - index))
          },
        ),
      ])
    }
    let result = otherLocales.map(otherLocale => [
      otherLocale,
      matchScore(otherLocale),
    ])
    result = result.filter(([, score]) => score > 0)
    result = maxBy(result, ([, score]) => score)
    result = result && result[0]
    return (
      result ||
      (criteria.useDefault &&
        otherLocales.find(
          otherLocale => otherLocale.isDefault,
        ))
    )
  }
}

export function speak(text, language, options = {}) {
  return new Promise((resolve, reject) => {
    let utterance = new SpeechSynthesisUtterance(text)
    utterance.lang = Locale.parse(language)
      .format`${'language'}_${'region'}`
    utterance.rate = 0.75
    Object.assign(utterance, options)
    if (!isObject(utterance.voice)) {
      let voices = getVoices(language)
      let localVoices = voices.filter(
        voice => voice.localService,
      )
      if (localVoices.length) {
        voices = localVoices
      }
      utterance.voice = voices[0]
    }
    window.speechSynthesis.speak(utterance)
    utterance.addEventListener('end', () => {
      resolve('end')
    })
    utterance.addEventListener('error', () => {
      reject()
    })
  })
}

export function canSpeak(language) {
  if (!language) {
    return !!window.speechSynthesis
  }
  let locale = Locale.parse(language)
  return !!(
    window.speechSynthesis &&
    locale.match(getSupportedLocales())
  )
}

export function getSupportedLocales() {
  return window.speechSynthesis
    .getVoices()
    .map(voice => Locale.parse(voice.lang))
    .filter(locale => locale)
}

export function getVoices(language) {
  let locale = Locale.parse(language)
  return (
    locale &&
    window.speechSynthesis.getVoices().filter(voice => {
      let locale2 = Locale.parse(voice.lang)
      return locale2 && locale2.match(locale)
    })
  )
}
